var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from "react";
import { Col, MessageBanner, Row } from "@folio/stripes/components";
import { useIntl } from "react-intl";
import { RecekSelect, RecekSelection } from "../../components/inputs";
import { derivePublicationForm, formatFieldsForPublicationForm, formatFieldsForSelection, formatsOfPublicationForm, hasMultipleProductFormats, } from "../../utils/workSpecificationUtils";
import { getPrintedPublicationTranslations } from "../../utils/getPrintedPublicationTranslations";
import { getEbookTranslations } from "../../utils/getEbookTranslations";
import { getDataCarrierVersionTranslations } from "../../utils/getDataCarrierVersionTranslations";
import { getAudiobookTranslations } from "../../utils/getAudiobookTranslations";
var PUBLICATION_FORM_OPTIONS = [
    {
        value: "PRINT",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.publicationForm.print",
    },
    {
        value: "EBOOK",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.publicationForm.ebook",
    },
    {
        value: "DATA_CARRIER",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.publicationForm.dataCarrier",
    },
    {
        value: "AUDIOBOOK",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.publicationForm.audiobook",
    },
];
var REFINING_SELECTS = {
    PRINT: {
        name: "workSpecification.publicationType.printedPublications",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.printedPublicationBinding",
        errorKey: "printedPublications",
        options: [
            {
                value: "SOFTCOVER",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.printedPublicationBinding.soft",
            },
            {
                value: "HARDCOVER",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.printedPublicationBinding.hard",
            },
        ],
    },
    EBOOK: {
        name: "workSpecification.eBookFormats",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.ebookFormats",
        errorKey: "eBookFormats",
        options: [
            {
                value: "PDF",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.ebookFormats.pdf",
            },
            {
                value: "EPUB",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.ebookFormats.epub",
            },
            {
                value: "MOBI",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.ebookFormats.mobi",
            },
        ],
    },
    DATA_CARRIER: {
        name: "workSpecification.dataCarrierVersions",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.dataCarrierType",
        errorKey: "dataCarrierVersions",
        options: [
            {
                value: "CD_ROM",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.dataCarrierVersion.cd",
            },
            {
                value: "DVD_ROM",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.dataCarrierVersion.dvd",
            },
        ],
    },
    AUDIOBOOK: {
        name: "workSpecification.audioBookFormats",
        labelId: "ui-br-publishers.publishers.workSpec.select.label.audioBookForm",
        errorKey: "audioBookFormats",
        options: [
            {
                value: "ON_CARRIER",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.audioBook.on",
            },
            {
                value: "WITHOUT_CARRIER",
                labelId: "ui-br-publishers.publishers.workSpec.select.label.audioBook.off",
            },
        ],
    },
};
var describeFormats = function (intl, workSpecification) {
    return __spreadArray(__spreadArray(__spreadArray(__spreadArray([], formatsOfPublicationForm(workSpecification, "PRINT").map(function (value) {
        return getPrintedPublicationTranslations(intl, value);
    }), true), formatsOfPublicationForm(workSpecification, "EBOOK").map(function (value) {
        return getEbookTranslations(intl, value);
    }), true), formatsOfPublicationForm(workSpecification, "DATA_CARRIER").map(function (value) { return getDataCarrierVersionTranslations(intl, value); }), true), formatsOfPublicationForm(workSpecification, "AUDIOBOOK").map(function (value) {
        return getAudiobookTranslations(intl, value);
    }), true).join(", ");
};
/**
 * The format part of the one-off work specification: one required publication form
 * (print / e-book / data carrier / audiobook) and one required refining select for it,
 * so a request always yields exactly one product in the agency (RECEK-74182). Choosing a
 * form or a refining value clears the formats of the other forms. A request stored earlier
 * with several formats is shown with a warning and kept as it is until the user changes it.
 */
export var WorkSpecFormatFields = function (_a) {
    var _b, _c;
    var workSpecification = _a.workSpecification, errors = _a.errors, onChange = _a.onChange, otherFormats = _a.otherFormats, _d = _a.disabled, disabled = _d === void 0 ? false : _d;
    var intl = useIntl();
    var formatMessage = intl.formatMessage;
    var publicationForm = derivePublicationForm(workSpecification);
    var refining = publicationForm
        ? REFINING_SELECTS[publicationForm]
        : undefined;
    var refiningValue = publicationForm
        ? ((_b = formatsOfPublicationForm(workSpecification, publicationForm)[0]) !== null && _b !== void 0 ? _b : "")
        : "";
    var defaultOption = {
        value: "",
        label: formatMessage({
            id: "ui-br-publishers.publishers.select.label.default",
        }),
    };
    var handlePublicationFormChange = function (value) {
        onChange(formatFieldsForPublicationForm(workSpecification, value));
    };
    var handleRefiningChange = function (value) {
        if (!publicationForm)
            return;
        onChange(formatFieldsForSelection(workSpecification, publicationForm, value));
    };
    var refiningError = function (key) {
        var _a, _b, _c;
        return key === "printedPublications"
            ? (_b = (_a = errors === null || errors === void 0 ? void 0 : errors.publicationType) === null || _a === void 0 ? void 0 : _a.printedPublications) === null || _b === void 0 ? void 0 : _b.message
            : (_c = errors === null || errors === void 0 ? void 0 : errors[key]) === null || _c === void 0 ? void 0 : _c.message;
    };
    return (React.createElement(React.Fragment, null,
        hasMultipleProductFormats(workSpecification) && (React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(MessageBanner, { type: "warning" }, formatMessage({
                    id: "ui-br-publishers.publishers.workSpec.warning.multipleFormats",
                }, { formats: describeFormats(intl, workSpecification) }))))),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(RecekSelect, { name: "workSpecification.publicationForm", autoComplete: "off", required: true, disabled: disabled, value: publicationForm, onChange: function (event) {
                        return handlePublicationFormChange(event.target.value);
                    }, error: (_c = errors === null || errors === void 0 ? void 0 : errors.publicationForm) === null || _c === void 0 ? void 0 : _c.message, labelId: "ui-br-publishers.publishers.workSpec.select.label.publicationForm", dataOptions: __spreadArray([
                        defaultOption
                    ], PUBLICATION_FORM_OPTIONS.map(function (option) { return ({
                        value: option.value,
                        label: formatMessage({ id: option.labelId }),
                    }); }), true) }))),
        refining && (React.createElement(Row, null,
            React.createElement(Col, { xs: 12 },
                React.createElement(RecekSelection, { name: refining.name, autoComplete: "off", labelId: refining.labelId, value: refiningValue, onChange: handleRefiningChange, required: true, disabled: disabled, error: refiningError(refining.errorKey), dataOptions: __spreadArray([
                        defaultOption
                    ], refining.options.map(function (option) { return ({
                        value: option.value,
                        label: formatMessage({ id: option.labelId }),
                    }); }), true) })))),
        publicationForm === "EBOOK" && otherFormats));
};
